export type PriceLists = {
  prices: Price[]
  prices2: Price[]
  prices3: Price[]
}

/**
 * Normalizes any Google Sheets link to a public CSV endpoint.
 * Works with:
 * - Direct CSV export/published links
 * - Standard shareable edit links (https://docs.google.com/spreadsheets/d/<ID>/edit...)
 */
export function normalizeGoogleSheetUrl(rawUrl: string): string {
  const trimmed = rawUrl.trim()
  if (!trimmed) return ''

  // If it's already a published CSV or export CSV link, return as is
  if (trimmed.includes('output=csv') || trimmed.includes('format=csv') || trimmed.includes('tqx=out:csv')) {
    return trimmed
  }

  // Extract spreadsheet ID and optional gid from standard sharing URL
  const match = trimmed.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/)
  if (match && match[1]) {
    const sheetId = match[1]
    const gidMatch = trimmed.match(/[?&#]gid=([0-9]+)/)
    const gidParam = gidMatch ? `&gid=${gidMatch[1]}` : ''
    return `https://docs.google.com/spreadsheets/d/${sheetId}/gviz/tq?tqx=out:csv${gidParam}`
  }

  return trimmed
}

/**
 * Robust CSV parser that handles quotes, escaped quotes, and multiline values.
 */
export function parseCSV(text: string): string[][] {
  const rows: string[][] = []
  let currentRow: string[] = []
  let currentField = ''
  let inQuotes = false

  for (let i = 0; i < text.length; i++) {
    const char = text[i]
    const nextChar = text[i + 1]

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        currentField += '"'
        i++ // Skip escaped quote
      } else {
        inQuotes = !inQuotes
      }
    } else if (char === ',' && !inQuotes) {
      currentRow.push(currentField.trim())
      currentField = ''
    } else if ((char === '\r' || char === '\n') && !inQuotes) {
      if (char === '\r' && nextChar === '\n') {
        i++ // Skip \n in CRLF
      }
      currentRow.push(currentField.trim())
      if (currentRow.some((f) => f.length > 0)) {
        rows.push(currentRow)
      }
      currentRow = []
      currentField = ''
    } else {
      currentField += char
    }
  }

  if (currentField.length > 0 || currentRow.length > 0) {
    currentRow.push(currentField.trim())
    if (currentRow.some((f) => f.length > 0)) {
      rows.push(currentRow)
    }
  }

  return rows
}

/**
 * Parses CSV rows into the 3 box price lists.
 */
export function parsePricesCSV(csvContent: string): PriceLists {
  const rows = parseCSV(csvContent)
  const result: PriceLists = {
    prices: [],
    prices2: [],
    prices3: [],
  }

  if (rows.length < 2) {
    return result
  }

  // Find column indexes from header row (case/accent insensitive)
  const headers = rows[0].map((h) =>
    h
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
  )

  let sizeIdx = headers.findIndex((h) => h.includes('tamano') || h.includes('size') || h.includes('categoria'))
  let daysIdx = headers.findIndex((h) => h.includes('dia') || h.includes('day') || h.includes('tiempo'))
  let priceIdx = headers.findIndex((h) => h.includes('precio') || h.includes('price') || h.includes('monto') || h.includes('valor'))
  let promoIdx = headers.findIndex((h) => h.includes('promo') && !h.includes('detalle') && !h.includes('long'))
  let promoLongIdx = headers.findIndex((h) => h.includes('detalle') || h.includes('long') || h.includes('descripcion'))
  let keyIdx = headers.findIndex((h) => h.includes('total') || h.includes('clave') || h.includes('key'))

  // Fallbacks if headers weren't named standardly
  if (sizeIdx === -1) sizeIdx = 0
  if (daysIdx === -1) daysIdx = 1
  if (priceIdx === -1) priceIdx = 2
  if (promoIdx === -1) promoIdx = 3
  if (promoLongIdx === -1) promoLongIdx = 4

  for (let r = 1; r < rows.length; r++) {
    const row = rows[r]
    if (!row || row.length <= Math.max(sizeIdx, daysIdx, priceIdx)) continue

    const rawSize = (row[sizeIdx] || '').toLowerCase()
    const rawDays = row[daysIdx] || ''
    const rawPrice = row[priceIdx] || ''
    const rawPromo = promoIdx < row.length ? row[promoIdx] || '' : ''
    const rawPromoLong = promoLongIdx >= 0 && promoLongIdx < row.length ? row[promoLongIdx] || '' : ''
    const rawKey = keyIdx >= 0 && keyIdx < row.length ? row[keyIdx] || '' : ''

    const daysNum = parseInt(rawDays.replace(/[^0-9]/g, ''), 10)
    const priceNum = parseInt(rawPrice.replace(/[^0-9]/g, ''), 10)

    if (isNaN(daysNum) || isNaN(priceNum) || priceNum <= 0) {
      continue
    }

    let key = daysNum
    if (rawKey && !isNaN(parseInt(rawKey.replace(/[^0-9]/g, ''), 10))) {
      key = parseInt(rawKey.replace(/[^0-9]/g, ''), 10)
    } else if (rawPromo.includes('+10') || rawPromo.includes('10')) {
      key = daysNum + 10
    } else if (rawPromo.includes('+30') || rawPromo.includes('30')) {
      key = daysNum + 30
    }

    let promo = rawPromo.trim() || undefined
    let promo_long = rawPromoLong.trim() || undefined
    if (promo && !promo_long) {
      promo_long = `Se añaden al plazo contratado ${promo.replace('+', '')} bonificados`
    }

    const item: Price = {
      key,
      text: `${daysNum} día${daysNum > 1 ? 's' : ''}`,
      price: priceNum,
      promo,
      promo_long,
    }

    if (rawSize.includes('peque') || rawSize.includes('1') || rawSize.includes('chico')) {
      result.prices.push(item)
    } else if (rawSize.includes('media') || rawSize.includes('2')) {
      result.prices2.push(item)
    } else if (rawSize.includes('gran') || rawSize.includes('3')) {
      result.prices3.push(item)
    }
  }

  // Sort by key ascending
  result.prices.sort((a, b) => a.key - b.key)
  result.prices2.sort((a, b) => a.key - b.key)
  result.prices3.sort((a, b) => a.key - b.key)

  return result
}

/**
 * Fetches and parses prices from Google Sheets CSV.
 */
export async function fetchPricesFromGoogleSheet(sheetUrl: string): Promise<PriceLists> {
  const normalizedUrl = normalizeGoogleSheetUrl(sheetUrl)
  if (!normalizedUrl) {
    throw new Error('Google Sheet URL is invalid or empty')
  }

  // Append timestamp parameter to bypass browser/CDN caches
  const cacheBusterUrl = normalizedUrl.includes('?')
    ? `${normalizedUrl}&_t=${Date.now()}`
    : `${normalizedUrl}?_t=${Date.now()}`

  const response = await fetch(cacheBusterUrl)
  if (!response.ok) {
    throw new Error(`Failed to fetch Google Sheet: HTTP ${response.status}`)
  }

  const csvContent = await response.text()
  return parsePricesCSV(csvContent)
}
